import React, { useEffect, useRef, useContext } from "react";
import { LMDrawingContext } from '../contexts/AppContext';

function LoftAttack({ selectedShot, selectedSetup }) {
  const canvasRef = useRef(null);
  const { lmDrawing } = useContext(LMDrawingContext);
  const imageCache = {};

  function getCachedImage(src) {
    // if we've already created an Image for this src, reuse it
    if (imageCache[src]) {
      return imageCache[src];
    }

    // otherwise create it once, store it, and return it
    const img = new Image();
    img.src = src;
    imageCache[src] = img;
    return img;
  }

  function getRearViewHeadImage(setup) {
    const clubCategory = (setup?.ClubCategory || '').toLowerCase();
    const clubDescription = (setup?.ClubDescription || '').toLowerCase();
    const pcCategoryId = setup?.PCCategoryId;

    if (clubCategory.includes('hybrid') || pcCategoryId === "13") {
      return "/heads/hybridrear.png";
    }

    if (clubCategory.includes('wedge') || pcCategoryId === "4") {
      return "/heads/wedgerear.png";
    }

    if (clubCategory.includes('iron') || pcCategoryId === "2") {
      return "/heads/ironrear.png";
    }

    if (
      clubCategory.includes('fairway') ||
      (clubCategory.includes('wood') && !clubCategory.includes('driver')) ||
      clubDescription.includes('wd')
    ) {
      return "/heads/fairwayrear.png";
    }

    return "/heads/rear.png";
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let loft = 0;
    let attack = 0;
    let hasLoftAttack = false;

    if (selectedShot) {
      hasLoftAttack =
        selectedShot.LMInfos?.some(item => {
          if (item.LMType === lmDrawing &&
            "ImpactPitchAngleDeg" in item.Measurements &&
            "ImpactAttackAngleDeg" in item.Measurements
          ) {
            loft = item.Measurements["ImpactPitchAngleDeg"];
            attack = item.Measurements["ImpactAttackAngleDeg"];
            return true;
          }
          else {
            loft = 0;
            attack = 0;
            return false;
          }
        }
        )
        ;
    } else {
      hasLoftAttack = false;
    }

    const ctx = canvas.getContext("2d");
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // --- Helpers bound to this canvas/context ---
    function drawArrowhead(x, y, angle, color) {
      const size = 15;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x - 2, y);
      ctx.lineTo(
        x - size * Math.cos(angle - Math.PI / 6),
        y - size * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        x - size * Math.cos(angle + Math.PI / 6),
        y - size * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    }

    function drawLine(angle, length, color, dashed = false, start = false, end = false) {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      const radians = angle;
      const endX = centerX + length * Math.cos(radians);
      const endY = centerY + length * Math.sin(radians);

      if (dashed) {
        ctx.setLineDash([5, 5]);
      } else {
        ctx.setLineDash([]);
      }

      ctx.lineTo(endX, endY);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();

      if (start) {
        drawArrowhead(endX, endY, radians, color);
      }
      if (end) {
        drawArrowhead(centerX, centerY, radians + Math.PI, color);
      }
    }

    function drawPoint(point, color) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(point.x, point.y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }

    const loftDeg = loft;
    const attackDeg = attack;

    // Convert to radians (and add π for your original alignment)
    const LoftAngle = loftDeg * (Math.PI / 180) + Math.PI;
    const AttackAngle = attackDeg * (Math.PI / 180);

    const ActualLoft = 10; // same constant as original
    const LoftAngle_1 = LoftAngle;
    const AttackAngle_2 = AttackAngle;

    const angleText = `${loftDeg.toFixed(1)}° Loft`;
    const angleText2 = `${attackDeg.toFixed(1)}° Attack`;

    const radius = 150;
    const width = 170;
    const height = 200;

    // --- Arc angle setup (ported from your script) ---
    let LoftStartAngle, LoftEndAngle, LoftDir;
    if (LoftAngle_1 <= Math.PI) {
      LoftStartAngle = LoftAngle_1;
      LoftEndAngle = Math.PI;
      LoftDir = false; // clockwise
    } else {
      LoftStartAngle = LoftAngle_1;
      LoftEndAngle = Math.PI;
      LoftDir = true; // counter-clockwise
    }

    let AttackStartAngle2, AttackEndAngle2, AttackDir;
    if (AttackAngle_2 <= 0) {
      AttackStartAngle2 = AttackAngle_2;
      AttackEndAngle2 = 0;
      AttackDir = false;
    } else {
      AttackStartAngle2 = AttackAngle_2;
      AttackEndAngle2 = 0;
      AttackDir = true;
    }

    let LtAstartAngle, LtAendAngle, LtADir = true;
    if (AttackAngle_2 + Math.PI > LoftAngle_1) {
      LtAstartAngle = AttackAngle_2 + Math.PI;
      LtAendAngle = LoftAngle_1;
    } else {
      LtAendAngle = AttackAngle_2 + Math.PI;
      LtAstartAngle = LoftAngle_1;
    }

    const textX =
      centerX +
      radius * Math.cos((LoftStartAngle + LoftEndAngle) / 2) - 30;
    const textY =
      centerY -
      (radius + 10) * Math.sin((LoftStartAngle + LoftEndAngle) / 2) -
      100;

    const lighterLightBlue = "hsl(195, 53%, 90%, 0.2)";
    const lighterLightPink = "rgba(255, 228, 232, 0.2)";
    const lighterLightOrange = "hsla(30, 100%, 80%, 0.2)";

    // --- Load image, then draw everything ---
    const clubImage = getRearViewHeadImage(selectedSetup);
    const img = getCachedImage(clubImage);
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Rotated club image
      ctx.save();
      ctx.translate(centerX - width / 2 + 40, centerY);
      ctx.rotate(LoftAngle_1 - Math.PI - (ActualLoft * Math.PI) / 180);
      ctx.drawImage(img, -10, (-2 * height) / 3, width, height);
      ctx.restore();

      if (hasLoftAttack) {
        ctx.textAlign = "left";

        drawPoint(
          { x: centerX - width / 2 + 44, y: centerY },
          "magenta"
        );

        // --- Loft (blue) arc + fill + label ---
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, LoftStartAngle, LoftEndAngle, LoftDir);
        ctx.strokeStyle = "cyan";
        ctx.lineWidth = 2;
        ctx.stroke();


        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, LoftStartAngle, LoftEndAngle, LoftDir);
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = lighterLightBlue;
        ctx.fill();

        ctx.fillStyle = "cyan";
        ctx.font = "bold 18px 'Comfortaa'";
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 3;
        ctx.strokeText(angleText, textX, textY);
        ctx.fillText(angleText, textX, textY);

        // --- Attack (magenta) arc + fill + label ---
        ctx.beginPath();
        ctx.arc(
          centerX,
          centerY,
          radius,
          AttackStartAngle2,
          AttackEndAngle2,
          AttackDir
        );
        ctx.strokeStyle = "magenta";
        ctx.lineWidth = 2;
        ctx.stroke();

        const textX2 =
          centerX +
          (radius + 10) *
          Math.cos((AttackStartAngle2 + AttackEndAngle2) / 2) + 20;
        const textY2 =
          centerY +
          (radius + 400) *
          Math.sin((AttackStartAngle2 + AttackEndAngle2) / 2) + 20;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(
          centerX,
          centerY,
          radius,
          AttackStartAngle2,
          AttackEndAngle2,
          AttackDir
        );
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = lighterLightPink;
        ctx.fill();

        ctx.fillStyle = "magenta";
        ctx.font = "bold 18px 'Comfortaa'";
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 3;
        ctx.textAlign = 'right';
        ctx.strokeText(angleText2, textX2, textY2);
        ctx.fillText(angleText2, textX2, textY2);

        // --- Baselines ---
        drawLine(0, 200, "orange", true, false, false);
        drawLine(Math.PI, 200, "orange", true, false, false);

        // Loft & attack vectors
        drawLine(LoftAngle_1, 200, "cyan", false, true, false);
        drawLine(AttackAngle_2, 200, "magenta", false, false, true);

        // --- Delta arc (orange) ---
        ctx.beginPath();
        ctx.arc(
          centerX,
          centerY,
          radius + 20,
          LtAstartAngle,
          LtAendAngle,
          LtADir
        );
        ctx.strokeStyle = "orange";
        ctx.lineWidth = 5;
        ctx.setLineDash([]);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(
          centerX,
          centerY,
          radius + 20,
          LtAstartAngle,
          LtAendAngle,
          LtADir
        );
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = lighterLightOrange;
        ctx.fill();
      } else {
        ctx.fillStyle = "#00000049"
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = "#ffffffff";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText("No Loft, Attack", centerX, centerY);
      }


    };

    img.src = clubImage;

  }, [selectedShot, selectedSetup]);

  return (
    <>
      <canvas
        ref={canvasRef}
        width={400}
        height={300}
      />
    </>
  );
}

export default LoftAttack;
